/**
 * @module useResumeQuoteAddress
 * @description
 * When the `prepopulateResumeAddress` Monarch flag is enabled (or the
 * ?prepopulateAddress=true query param is present), this hook fetches the
 * resume-quote address for the current user and returns it so the hero
 * ServiceCheckForm can pre-fill the address input.
 *
 * The feature can be toggled on/off via the Monarch ruleset key
 * `prepopulateResumeAddress` (boolean), or forced on for testing with
 * ?prepopulateAddress=true in the URL.
 */
import { useContext, useState, useEffect } from "react";
import { FuelContext } from "@/providers/fuel-provider";
import { getCookie } from "@/js/utils/cookie";
import { getClientSideTrackingIds } from "@/js/helpers/cart";
import { WRITE_KEY } from "@/js/constants/cohesion";

/**
 * @function useResumeQuoteAddress
 * @returns {{ prefillAddress: string|null, isPrefillLoading: boolean }}
 */
export const useResumeQuoteAddress = () => {
  const { prepopulateResumeAddress, olVersion } = useContext(FuelContext);
  const [prefillAddress, setPrefillAddress] = useState(null);
  const [isPrefillLoading, setIsPrefillLoading] = useState(false);

  useEffect(() => {
    if (!prepopulateResumeAddress) return;

    const quoteId = getCookie("quoteId");

    if (!quoteId || quoteId.length !== 36) return;

    const fetchResumeAddress = async () => {
      setIsPrefillLoading(true);

      try {
        const { clientSessionId } = getClientSideTrackingIds();

        const resumeResponse = await fetch(
          `${process.env.predictiveSearchUrl}/api/${olVersion}/quote/resume`,
          {
            method: "get",
            headers: {
              quoteId,
              "x-write-key": WRITE_KEY,
              "x-client-session-id": clientSessionId,
              "x-client-id": `fuel-cart-${process.env.environment}-frdc`,
            },
          },
        );

        if (!resumeResponse?.ok) return;

        const data = await resumeResponse.json();
        const { address, address2, zip } = data?.serviceAddress || {};

        if (address && zip) {
          const formatted = `${address}${address2 ? ` ${address2}` : ""}, ${zip}`;
          setPrefillAddress(formatted);
        }
      } catch (error) {
        console.error(
          "useResumeQuoteAddress: failed to fetch resume quote address",
          error,
        );
      } finally {
        setIsPrefillLoading(false);
      }
    };

    fetchResumeAddress();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prepopulateResumeAddress]);

  return { prefillAddress, isPrefillLoading };
};

export default useResumeQuoteAddress;
